import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Copy, Check, TrendingUp, Brain, BookOpen, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext, AppSettingsContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateBanners = () => {
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState(null);
  const [bannerData, setBannerData] = useState({});
  const [editingCode, setEditingCode] = useState(null);
  const [editCodeValue, setEditCodeValue] = useState('');
  const [uploading, setUploading] = useState(null);

  const appName = appSettings?.app_name || 'BettingTracker.Pro';
  const homeUrl = window.location.origin;
  const tipsterUrl = `${homeUrl}/become-tipster`;

  useEffect(() => {
    loadBannerData();
  }, []);

  const loadBannerData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/affiliate-banners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transform array to object keyed by theme-size
      const bannersObj = {};
      if (Array.isArray(response.data)) {
        response.data.forEach(banner => {
          const key = `${banner.theme}-${banner.size}`;
          bannersObj[key] = {
            image_url: `${BACKEND_URL}${banner.image_path}`,
            embed_code: banner.embed_code || generateEmbedCodeForBanner(banner),
            ...banner
          };
        });
      }
      setBannerData(bannersObj);
    } catch (error) {
      console.error('Error loading banner data:', error);
    }
  };

  const generateEmbedCodeForBanner = (banner) => {
    const themeMap = {
      'betting_tracker': { name: 'Betting Tracker', link: homeUrl },
      'tipster': { name: 'Tipster Opportunity', link: tipsterUrl },
      'ai_predictions': { name: 'AI Predictions', link: homeUrl },
      'betting_strategies': { name: 'Betting Strategies', link: homeUrl }
    };
    const theme = themeMap[banner.theme] || { name: banner.theme, link: homeUrl };
    
    return `<!-- ${appName} - ${theme.name} (${banner.width}x${banner.height}) -->
<a href="${theme.link}?ref=affiliate" target="_blank" rel="noopener">
  <img src="${BACKEND_URL}${banner.image_path}" 
       alt="${theme.name} - ${appName}" 
       width="${banner.width}" 
       height="${banner.height}" 
       style="border:none; display:block;" />
</a>`;
  };

  const handleImageUpload = async (bannerId, file, linkUrl) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('banner', file);
    formData.append('banner_id', bannerId);
    formData.append('link_url', linkUrl);

    setUploading(bannerId);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/affiliate-banners/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Add cache buster to force reload of updated image
      const timestamp = new Date().getTime();
      const imageUrlWithCache = `${BACKEND_URL}${response.data.image_url}?v=${timestamp}`;
      
      setBannerData(prev => ({
        ...prev,
        [bannerId]: {
          ...prev[bannerId],
          image_url: imageUrlWithCache,
          embed_code: response.data.embed_code
        }
      }));
      
      toast.success('Banner uploaded and embed code updated!');
    } catch (error) {
      console.error('Error uploading banner:', error);
      toast.error('Failed to upload banner');
    } finally {
      setUploading(null);
    }
  };

  const handleSaveEmbedCode = async (bannerId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${BACKEND_URL}/api/admin/affiliate-banners/embed-code`,
        {
          banner_id: bannerId,
          embed_code: editCodeValue
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setBannerData(prev => ({
        ...prev,
        [bannerId]: {
          ...prev[bannerId],
          embed_code: editCodeValue
        }
      }));
      
      setEditingCode(null);
      toast.success('Embed code saved!');
    } catch (error) {
      console.error('Error saving embed code:', error);
      toast.error('Failed to save embed code');
    }
  };

  // Banner configurations
  const bannerSizes = [
    { width: 728, height: 90, name: 'Leaderboard Banner' },
    { width: 468, height: 60, name: 'Full Banner' },
    { width: 300, height: 250, name: 'Medium Rectangle' },
    { width: 250, height: 250, name: 'Square Banner' }
  ];

  const bannerThemes = [
    {
      id: 'betting_tracker',
      title: 'Betting Tracker',
      subtitle: 'Track, Analyze, Win',
      description: 'Professional betting management for serious bettors',
      cta: 'Free Access',
      link: homeUrl,
      gradient: 'from-blue-600 to-cyan-500',
      icon: Activity,
      color: 'blue'
    },
    {
      id: 'tipster',
      title: 'Tipster Opportunity',
      subtitle: 'Turn Your Knowledge into Income',
      description: 'Share tips, build following, earn money',
      cta: 'Get Started',
      link: tipsterUrl,
      gradient: 'from-purple-600 to-pink-500',
      icon: TrendingUp,
      color: 'purple'
    },
    {
      id: 'ai_predictions',
      title: 'AI Predictions',
      subtitle: 'Smart Betting Insights',
      description: 'AI-powered predictions with detailed analysis',
      cta: 'Free Access',
      link: homeUrl,
      gradient: 'from-blue-600 to-cyan-500',
      icon: Brain,
      color: 'blue'
    },
    {
      id: 'betting_strategies',
      title: 'Betting Strategies',
      subtitle: 'Learn & Master',
      description: 'Comprehensive guides and winning strategies',
      cta: 'Free Access',
      link: homeUrl,
      gradient: 'from-blue-600 to-cyan-500',
      icon: BookOpen,
      color: 'blue'
    }
  ];

  const generateBanner = (size, theme) => {
    const Icon = theme.icon;
    const isVerySmall = size.width <= 250;
    const isSmall = size.width <= 468;
    const isSquare = size.width === size.height;
    
    // Calculate responsive sizing
    const padding = isVerySmall ? '8px' : isSmall ? '12px' : '16px';
    const iconSize = isVerySmall ? '16px' : isSmall ? '20px' : '28px';
    const titleSize = isVerySmall ? '11px' : isSmall ? '14px' : '20px';
    const subtitleSize = isVerySmall ? '9px' : isSmall ? '11px' : '14px';
    const ctaSize = isVerySmall ? '9px' : isSmall ? '11px' : '13px';
    const ctaPadding = isVerySmall ? '4px 10px' : isSmall ? '6px 14px' : '8px 20px';
    
    return (
      <div 
        style={{ 
          width: `${size.width}px`, 
          height: `${size.height}px`,
          padding: padding
        }}
        className={`relative overflow-hidden rounded-lg shadow-xl bg-gradient-to-br ${theme.gradient} cursor-pointer transition-transform hover:scale-105`}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>

        {/* Content - Centered Layout */}
        <div className="relative z-10 flex flex-col items-center justify-center text-center h-full w-full">
          {/* Icon */}
          <Icon style={{ width: iconSize, height: iconSize }} className="text-white mb-2" />
          
          {/* Title */}
          <h3 
            style={{ 
              fontSize: titleSize,
              lineHeight: isVerySmall ? '1.2' : '1.3',
              marginBottom: isVerySmall ? '4px' : '6px'
            }}
            className="font-bold text-white"
          >
            {theme.title}
          </h3>
          
          {/* Subtitle - Hide on very small banners */}
          {!isVerySmall && (
            <p 
              style={{ 
                fontSize: subtitleSize,
                lineHeight: '1.3',
                marginBottom: '8px'
              }}
              className="font-semibold text-white/95"
            >
              {theme.subtitle}
            </p>
          )}
          
          {/* Description - Only show on square/tall banners */}
          {size.height >= 250 && !isSmall && (
            <p 
              style={{ 
                fontSize: '12px',
                lineHeight: '1.4',
                marginBottom: '12px',
                maxWidth: '80%'
              }}
              className="text-white/90"
            >
              {theme.description}
            </p>
          )}
          
          {/* CTA Button */}
          <div 
            style={{ 
              fontSize: ctaSize,
              padding: ctaPadding,
              marginTop: isVerySmall ? '4px' : '8px'
            }}
            className="inline-block bg-white text-gray-900 rounded-full font-bold shadow-lg hover:shadow-xl transition-shadow"
          >
            {theme.cta}
          </div>
          
          {/* App Name Badge - Only on larger banners */}
          {!isSmall && (
            <div 
              style={{ 
                fontSize: '10px',
                marginTop: '12px'
              }}
              className="text-white/80 font-semibold"
            >
              {appName}
            </div>
          )}
        </div>
      </div>
    );
  };

  const generateEmbedCode = (size, theme) => {
    const bannerId = `${theme.id}-${size.width}x${size.height}`;
    return `<!-- ${appName} - ${theme.title} (${size.width}x${size.height}) -->
<a href="${theme.link}?ref=affiliate" target="_blank" rel="noopener">
  <img src="${homeUrl}/banners/${bannerId}.png" 
       alt="${theme.title} - ${appName}" 
       width="${size.width}" 
       height="${size.height}" 
       style="border:none; display:block;" />
</a>`;
  };

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Embed code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
          <div className="text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Admin Access Required</h2>
            <Button onClick={() => navigate('/admin/dashboard')} className="bg-purple-600 hover:bg-purple-700">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="mb-4 border-white/20 text-white hover:bg-white/10"
            >
              ← Back to Dashboard
            </Button>
            <h1 className="text-4xl font-bold text-white mb-2">Affiliate Marketing Banners</h1>
            <p className="text-gray-300">Professional banners for promoting {appName}</p>
          </div>

          {/* Info Card */}
          <Card className="glass-dark border-white/10 mb-8">
            <CardContent className="pt-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-2">How to Use:</h3>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Right-click on any banner and "Save Image As" to download</li>
                  <li>• Copy the embed code below each banner</li>
                  <li>• Paste the code on your website where you want the banner to appear</li>
                  <li>• Banners link to relevant pages with affiliate tracking</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Banners by Size */}
          {bannerSizes.map((size) => (
            <Card key={`${size.width}x${size.height}`} className="glass-dark border-white/10 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  {size.name} ({size.width}x{size.height})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  {bannerThemes.map((theme) => {
                    const bannerId = `${theme.id}-${size.width}x${size.height}`;
                    const defaultEmbedCode = generateEmbedCode(size, theme);
                    const savedData = bannerData[bannerId] || {};
                    const embedCode = savedData.embed_code || defaultEmbedCode;
                    const hasBannerImage = savedData.image_url;
                    
                    return (
                      <div key={bannerId} className="space-y-4">
                        {/* Theme Label */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 bg-${theme.color}-500 rounded-full`}></div>
                            <h4 className="text-white font-semibold">{theme.title}</h4>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            {/* Upload Button */}
                            <input
                              type="file"
                              accept="image/*"
                              id={`upload-${bannerId}`}
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleImageUpload(bannerId, file, theme.link);
                                  e.target.value = ''; // Reset input
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              onClick={() => document.getElementById(`upload-${bannerId}`).click()}
                              disabled={uploading === bannerId}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              {uploading === bannerId ? '⏳ Uploading...' : '📤 Upload Custom'}
                            </Button>
                            
                            {/* Download Button */}
                            {hasBannerImage && (
                              <a
                                href={savedData.image_url}
                                download={`${theme.title.replace(/\s+/g, '_')}_${size.width}x${size.height}.png`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  📥 Download
                                </Button>
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Banner Preview */}
                        <div className="bg-slate-800/50 rounded-lg p-6 flex justify-center items-center overflow-auto">
                          {hasBannerImage ? (
                            <a 
                              href={theme.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-block cursor-pointer"
                            >
                              <img 
                                src={savedData.image_url}
                                alt={`${theme.title} Banner`}
                                width={size.width}
                                height={size.height}
                                className="rounded-lg shadow-xl"
                                style={{ 
                                  display: 'block',
                                  width: `${size.width}px`,
                                  height: `${size.height}px`,
                                  imageRendering: '-webkit-optimize-contrast',
                                  objectFit: 'contain',
                                  msInterpolationMode: 'nearest-neighbor'
                                }}
                              />
                            </a>
                          ) : (
                            <div className="text-white text-center">
                              <p>Loading banner...</p>
                            </div>
                          )}
                        </div>

                        {/* Embed Code */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm text-gray-400">Embed Code:</label>
                            <div className="flex gap-2">
                              {editingCode === bannerId ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleSaveEmbedCode(bannerId)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <Check className="w-4 h-4 mr-2" />
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingCode(null)}
                                    className="border-white/20 text-white hover:bg-white/10"
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setEditingCode(bannerId);
                                      setEditCodeValue(embedCode);
                                    }}
                                    className="bg-yellow-600 hover:bg-yellow-700"
                                  >
                                    ✏️ Edit Code
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => copyToClipboard(embedCode, bannerId)}
                                    className="bg-purple-600 hover:bg-purple-700"
                                  >
                                    {copiedCode === bannerId ? (
                                      <>
                                        <Check className="w-4 h-4 mr-2" />
                                        Copied!
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="w-4 h-4 mr-2" />
                                        Copy Code
                                      </>
                                    )}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          {editingCode === bannerId ? (
                            <textarea
                              value={editCodeValue}
                              onChange={(e) => setEditCodeValue(e.target.value)}
                              rows={6}
                              className="w-full bg-slate-900 border border-white/20 rounded-lg p-4 text-xs text-gray-300 font-mono"
                            />
                          ) : (
                            <div className="bg-slate-900 border border-white/10 rounded-lg p-4 overflow-x-auto">
                              <code className="text-xs text-gray-300 whitespace-pre">
                                {embedCode}
                              </code>
                            </div>
                          )}
                        </div>

                        {/* Divider */}
                        {bannerThemes.indexOf(theme) < bannerThemes.length - 1 && (
                          <div className="border-t border-white/10 mt-6"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Footer Note */}
          <Card className="glass-dark border-white/10">
            <CardContent className="pt-6">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-2">Note:</h3>
                <p className="text-gray-300 text-sm">
                  To use these banners on external websites, you'll need to save the banner images and upload them to your server or use the generated preview. 
                  Replace the image src in the embed code with your hosted image URL.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AffiliateBanners;
